import type { MultipleChoiceQuestion } from '@shared/domain/form';
import { LightbulbCircleOutlined } from '@mui/icons-material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CustomTooltip from '@shared/components/Tooltip';
import { motion } from 'framer-motion';
import { useId } from 'react';

interface MultipleChoiceQuestionProps {
  handleFormFieldChange: (e: any) => void;
  formFields: any;
  question: MultipleChoiceQuestion;
  errors?: any;
  isNarrow?: boolean;
  isWide?: boolean;
  disabled?: boolean;
  disabledOptions?: string[];
  isConditional?: boolean;
}

// Gentle settle, no scale pop — SCSS glow ring carries the attention.
const CONDITIONAL_ENTRANCE = {
  initial: { opacity: 0, y: -6 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] as const },
};

const DEFAULT_ENTRANCE = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.25, ease: 'easeOut' as const },
};

function MultipleChoiceReviewQuestion(props: MultipleChoiceQuestionProps) {
  const {
    handleFormFieldChange,
    formFields,
    question,
    disabled,
    errors,
    disabledOptions,
    isConditional = false,
  } = props;

  const groupId = useId();
  const errorMessage = errors && errors[question.name];
  const hasError = !!errorMessage;

  const motionProps = isConditional ? CONDITIONAL_ENTRANCE : DEFAULT_ENTRANCE;
  const className = [
    'review-question',
    isConditional && 'review-question--conditional',
    hasError && 'review-question--has-error',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <motion.section className={className} data-error-name={question.name} data-testid={question.name} {...motionProps}>
      <h3 id={`${groupId}-label`} className="review-question__prompt">
        <span>{question.question}</span>
        {question.tooltipText && (
          <CustomTooltip title={question.tooltipText} intent="help" icon={<LightbulbCircleOutlined />} />
        )}
      </h3>

      <div className="review-question__answer">
        <div role="group" aria-labelledby={`${groupId}-label`} className="review-options">
          {question.options.map((value) => {
            const isActive = formFields[question.name] === value;
            const isDisabled = disabled || (disabledOptions ? !disabledOptions.includes(value) : false);

            return (
              <button
                key={value}
                type="button"
                name={question.name}
                value={value}
                disabled={isDisabled}
                onClick={handleFormFieldChange}
                aria-pressed={isActive}
                className={`review-options__pill${isActive ? ' review-options__pill--active' : ''}`}
              >
                {isActive && <CheckRoundedIcon className="review-options__check-icon" fontSize="small" />}
                <span>{value}</span>
              </button>
            );
          })}
        </div>

        {hasError && (
          <div className="review-error" role="alert">
            <WarningAmberIcon />
            <span>{errorMessage}</span>
          </div>
        )}
      </div>
    </motion.section>
  );
}

export default MultipleChoiceReviewQuestion;
